import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';

import {
  Indicator,
  IndicatorCategory,
  IndicatorCategoryType,
} from '~/services/Http';

import { GET_INDICATORS_BY_UNIT_SWR_KEY } from '../../../../../../data';

import { getAllIndicators, updateIndicator } from './data';

export interface UseComponentOutput {
  editableIndicators: Indicator[];
  handleActiveStateChange: (
    indicatorId: number,
  ) => (_: React.ChangeEvent, checked: boolean) => void;
  handleConfigValueChange: (
    indicatorId: number,
  ) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  saveIndicators: () => void;
}

export const useComponent = (
  selectedCategory: IndicatorCategory,
  onSaved: () => void,
  unitId: number,
): UseComponentOutput => {
  const { data: allIndicators, mutate: mutateAllIndicators } = useSWR(
    ['/json/solaintel/indicator?includeInactive', true],
    getAllIndicators,
  );

  const [editableIndicators, setEditableIndicators] = useState<Indicator[]>([]);
  useEffect(() => {
    setEditableIndicators(
      allIndicators?.filter((a) => a.category === selectedCategory) || [],
    );
  }, [allIndicators, selectedCategory]);

  const handleActiveStateChange = useCallback(
    (indicatorId: number) => (_: React.ChangeEvent, checked: boolean) => {
      setEditableIndicators(
        editableIndicators.map((t) => {
          if (t.id === indicatorId) {
            return { ...t, active: checked };
          }

          return t;
        }),
      );
    },
    [editableIndicators],
  );

  const handleConfigValueChange = useCallback(
    (indicatorId: number) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const indicatorType = editableIndicators.find(
          (i) => i.id === indicatorId,
        )?.indicatorType;
        const onlyNums =
          indicatorType === IndicatorCategoryType.PERFORMANCE_SCORE_OVER ||
          indicatorType === IndicatorCategoryType.PERFORMANCE_SCORE_UNDER
            ? event.target.value
                // gets rid of everything but numbers and decimal point
                .replace(/[^0-9.]/g, '')
                // gets rid of the second decimal point, if any
                .replace(/(\..*)\./g, '$1')
                .slice(0, 4)
            : event.target.value.replace(/[^0-9]/g, '').slice(0, 4);

        setEditableIndicators(
          editableIndicators.map((t) => {
            if (t.id === indicatorId) {
              return {
                ...t,
                config: { [Object.keys(t.config)[0]]: onlyNums },
              };
            }

            return t;
          }),
        );
      },
    [editableIndicators],
  );

  const saveIndicators = useCallback(() => {
    const toBeSavedIndicators = editableIndicators.filter((e) => {
      const initialIndicator = allIndicators!.find((i) => i.id === e.id)!;

      let currentConfigValue = Object.values(e.config)[0];
      if (currentConfigValue?.endsWith('.')) {
        currentConfigValue = currentConfigValue.slice(0, -1);
      }

      return (
        e.active !== initialIndicator.active ||
        (currentConfigValue &&
          currentConfigValue !== Object.values(initialIndicator.config)[0])
      );
    });

    if (toBeSavedIndicators.length) {
      Promise.all(toBeSavedIndicators.map((i) => updateIndicator(i))).then(
        () => {
          globalMutate([GET_INDICATORS_BY_UNIT_SWR_KEY, unitId]);
          mutateAllIndicators();
          onSaved();
        },
      );
    }
  }, [editableIndicators, allIndicators, unitId, mutateAllIndicators, onSaved]);

  return {
    editableIndicators,
    handleActiveStateChange,
    handleConfigValueChange,
    saveIndicators,
  };
};
